import { useDialog } from '@repo/ui';
import { useToast } from '@zenterprise-inc/ui';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useMutation } from 'react-relay';

import { checkHometaxAccountMutation, ErrorType } from '@/__generated__/checkHometaxAccountMutation.graphql';
import { CorporationRefundData, getCorporationRefundData, setCorporationRefundData } from '@/components/tax-refund/common/data/corporation-refund-data';
import { getIndividualRefundData, setIndividualRefundData } from '@/components/tax-refund/common/data/individual-refund-data';
import { TAX_REFUND_ERROR } from '@/components/tax-refund/common/error/error';
import { checkHometaxAccount } from '@/graphql/mutation/taxRefund/checkHometaxAccount';
import { COMMON_PATHS, CORPORATION_PATHS, INDIVIDUAL_PATHS } from '@/lib/constants/paths';
import { refundTokenAtom } from '@/lib/stores/simple-auth';

export const useCheckHometaxAccount = () => {
  const [commit, isInFlight] = useMutation<checkHometaxAccountMutation>(checkHometaxAccount);
  const router = useRouter();
  const toast = useToast();
  const { showDialog } = useDialog();
  const setRefundToken = useSetAtom(refundTokenAtom);

  const handleCheckHometaxAccount = ({ variables, successAction, errorAction }: HandleCheckHometaxAccountProps) => {
    const refundData = variables.corpType === 'INDIS' ? getIndividualRefundData() : getCorporationRefundData();
    commit({
      variables,
      onCompleted: (response, errors) => {
        const { checkHometaxAccount } = response || {};

        if (!response.checkHometaxAccount) {
          console.error('Response object is empty.');
          return;
        } else if (errorAction && checkHometaxAccount.errors?.code) {
          if (checkHometaxAccount.errors?.code === TAX_REFUND_ERROR.홈택스세션오류) {
            showDialog({
              title: '홈택스 간편인증 로그인 지연 안내',
              description: '오후 6시 이후에 이용해주세요.\n홈택스 접속 이용자가 많아, 현재 로그인 인증이 원활하지 않아요.',
              buttons: {
                primary: { onClick: () => router.push(COMMON_PATHS.환급조회목록) }
              }
            });
          } else {
            errorAction(checkHometaxAccount.errors?.code, { email: checkHometaxAccount.email ?? '', lastLoginType: checkHometaxAccount.lastLoginType ?? '' });
          }
          return;
        } else if (errors) {
          console.log(errors);
          if (errors.length > 0) {
            toast({ content: errors[0]?.message, icon: 'error', iconColor: 'red.600' });
          }
          return;
        }

        if (checkHometaxAccount.result && checkHometaxAccount.refundToken) {
          setRefundToken(checkHometaxAccount.refundToken);

          if (variables.corpType === 'INDIS') {
            setIndividualRefundData({
              ...refundData,
              corpType: variables.corpType,
              authType: variables.loginType === 'CommonCert' ? 'joint-certificate' : variables.loginType === 'PartnerCert' ? 'partner-auth' : 'simple-auth',
              refundToken: checkHometaxAccount.refundToken,
              serviceAgreementFlow: !!refundData?.serviceAgreementFlow
            });
          } else {
            setCorporationRefundData({
              ...(refundData as CorporationRefundData),
              authType: variables.loginType === 'HometaxId' ? 'hometax' : 'joint-certificate',
              refundToken: checkHometaxAccount.refundToken,
              hometaxId: variables.hometaxAccount?.id,
              serviceAgreementFlow: !!refundData?.serviceAgreementFlow
            });
          }

          if (successAction) {
            successAction();
          } else {
            router.replace({
              pathname: variables.corpType === 'INDIS' ? INDIVIDUAL_PATHS.개인수집및조회 : CORPORATION_PATHS.법인수집및조회,
              query: { type: variables.corpType.toLowerCase() }
            });
          }
          return;
        }
      },
      onError: (error) => {
        toast({ content: '알 수 없는 오류입니다.', icon: 'error', iconColor: 'red.600' });
        console.error(error);
      }
    });
  };

  return { commit, isInFlight, handleCheckHometaxAccount };
};

export type AlreadyAppliedErrorDataType = { email: string; lastLoginType: string };

export type HandleCheckHometaxAccountProps = {
  variables: checkHometaxAccountMutation['variables'];
  successAction?: () => void;
  errorAction?: (errorType: ErrorType, errorData?: AlreadyAppliedErrorDataType) => void;
};
